/**
 * Tour steps (#776): order, the element each one points at and the page it opens. Words live in copy.ts.
 *
 * `targets` are CSS selectors tried in order; the first one that is rendered and visible wins (e.g. the trades card is
 * hidden behind a tab on phones, so that step falls back to the Book/Trades tabs). No target, or none visible after a
 * short wait: the step is shown as a centred card / bottom sheet without a spotlight.
 *
 * `page`: where the step must be shown. `catalog` = the market list, `market` = a live testnet market (picked at
 * runtime, see pickMarket.ts), `portfolio` = the portfolio (in ghost mode on a public address if no wallet is connected).
 * Undefined: stay on the current page.
 */
import type { TourCopyKey } from './copy';

export type TourPage = 'catalog' | 'market' | 'portfolio';

export interface TourStep {
  id: TourCopyKey;
  targets: readonly string[];
  page?: TourPage;
}

const t = (name: string) => `[data-tour="${name}"]`;

export const TOUR_STEPS: readonly TourStep[] = [
  { id: 'welcome', targets: [] },
  { id: 'env', targets: [t('env')] },
  { id: 'catalog', page: 'catalog', targets: [`${t('catalog-list')} > li:first-child`, t('catalog')] },
  { id: 'catalogControls', page: 'catalog', targets: [t('catalog-controls'), t('catalog')] },
  { id: 'marketSides', page: 'market', targets: [t('market-sides')] },
  { id: 'marketBook', page: 'market', targets: [t('market-book')] },
  { id: 'marketTrades', page: 'market', targets: [t('market-trades'), t('market-tabs')] },
  { id: 'ticket', page: 'market', targets: [t('ticket-sides'), t('ticket')] },
  { id: 'ticketPrice', page: 'market', targets: [t('ticket-price'), t('ticket')] },
  { id: 'ticketEconomics', page: 'market', targets: [t('ticket-economics'), t('ticket')] },
  { id: 'wallet', page: 'market', targets: [t('wallet')] },
  { id: 'portfolio', page: 'portfolio', targets: [t('portfolio')] },
  { id: 'finish', page: 'catalog', targets: [t('help')] },
];

/** The "how prediction contracts work" page (#777). */
export const LEARN_PATH = '/learn';

/**
 * A public, outcome-active testnet address (one of the ghost-mode quick picks) used for the portfolio step when no
 * wallet is connected. Read-only: ghost mode cannot sign or place orders.
 */
export const TOUR_GHOST_ADDRESS = '0x73513a6ab68a8fdc1a6262a4d34962ee9af8590c';
